import { useEffect, useState } from "react";
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ImageList,
  InputBase,
  Paper,
  Divider,
  ImageListItem,
  Box,
  ImageListItemBar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useAdminWordService } from "../../../hooks/useAdminWordService";
import { WordImage } from "../../../lib/WordService";

interface ImageSelectionDialogProps {
  images: WordImage[];
  word: string;
  isOpen: boolean;
  save: (images: WordImage[]) => void;
  onClose: () => void;
}

const ImageSelectionDialog = ({
  images,
  word,
  isOpen,
  save,
  onClose,
}: ImageSelectionDialogProps) => {
  const adminWordService = useAdminWordService();
  const [searchedImages, setSearchedImages] = useState<WordImage[]>([]);
  const [selectedImages, setSelectedImages] = useState<WordImage[]>(
    images || []
  );
  const [searchWord, setSearchWord] = useState<string>(word);

  const filterAndSetSearchImages = (images: WordImage[]) => {
    setSearchedImages(
      images.filter(
        (image) =>
          image?.url &&
          !(selectedImages || []).some((img) => img.url === image.url)
      )
    );
  };

  const imageSearchSubmit = async (
    searchWord: string,
    setSearchedImages: Function
  ) => {
    const results = await adminWordService.searchImages(searchWord);
    // Filter out any null/undefined results and those already selected
    const validResults = (results || []).filter((r) => r?.url);
    setSearchedImages(validResults);
  };

  useEffect(() => {
    if (isOpen) {
      imageSearchSubmit(word, filterAndSetSearchImages);
    }
    return () => filterAndSetSearchImages([]);
    // eslint-disable-next-line
  }, [isOpen, word]);

  const handleSearchWordChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearchWord(event.target.value);
  };

  const handleImageSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    imageSearchSubmit(searchWord, filterAndSetSearchImages);
  };

  const selectImage = (image: WordImage) => {
    setSelectedImages([...selectedImages, image]);
    setSearchedImages(
      (searchedImages ?? []).filter((img) => img.url !== image.url)
    );
  };

  const unselectImage = (image: WordImage) => {
    setSearchedImages([...(searchedImages ?? []), image]);
    setSelectedImages(selectedImages.filter((img) => img.url !== image.url));
  };

  const saveImages = () => {
    save(selectedImages);
    onClose();
  };

  const displayImage = (image: WordImage, index: number) => {
    return (
      <ImageListItem
        key={index}
        onClick={() => unselectImage(image)}
        sx={{ maxWidth: 100 }}
      >
        <img
          src={image.url}
          srcSet={image.url}
          loading="lazy"
          alt={`${image.keyword}-${index}`}
          title={image.keyword}
        />
      </ImageListItem>
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      scroll="paper"
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description"
    >
      <DialogTitle id="scroll-dialog-title">
        Image pour <strong>{word}</strong>
      </DialogTitle>
      <DialogContent dividers={true}>
        <Box id="scroll-dialog-description" tabIndex={-1}>
          {selectedImages && (
            <ImageList
              sx={{
                display: "flex",
                flexWrap: "nowrap",
                border: selectedImages.length > 0 ? 2 : 0,
                borderColor: "primary.main",
              }}
            >
              {selectedImages.map((image, index) => displayImage(image, index))}
            </ImageList>
          )}
          <Paper
            component="form"
            sx={{
              p: "2px 4px",
              display: "flex",
              mb: "24px",
              mx: "auto",
              alignItems: "center",
              width: 400,
            }}
          >
            <InputBase
              autoFocus
              sx={{ ml: 1, flex: 1 }}
              type="text"
              placeholder="Search"
              value={searchWord}
              onChange={handleSearchWordChange}
            />
            <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
            <IconButton
              type="submit"
              onClick={handleImageSearchSubmit}
              sx={{ p: "10px" }}
              aria-label="search"
            >
              <SearchIcon />
            </IconButton>
          </Paper>
          <ImageList cols={3}>
            {(searchedImages ?? []).map((imageLink, index) => (
              <Box key={index}>
                <ImageListItem onClick={() => selectImage(imageLink)}>
                  <img
                    src={imageLink.url}
                    srcSet={imageLink.url}
                    loading="lazy"
                    alt={`${imageLink.keyword}-${index}`}
                    title={imageLink.keyword}
                  />
                  <ImageListItemBar title={imageLink.keyword} />
                </ImageListItem>
              </Box>
            ))}
          </ImageList>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={saveImages}>Save</Button>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageSelectionDialog;
